import { HSLA } from '@ctrl/tinycolor'
import React, { useCallback, useMemo } from 'react'
import { ReactColorChangeResult } from 'src/shared/types/color-typings'

export interface ColorPickerAlphaProps {
  hsl: HSLA
  onChange: (colorChangeResult: ReactColorChangeResult) => void
}

export const ColorPickerHue = ({ hsl, onChange }: ColorPickerAlphaProps) => {
  const sliderRef = React.useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = React.useState(false)

  const calculateHue = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!sliderRef.current) {
        return
      }

      const container = sliderRef.current
      const containerWidth = container.clientWidth

      let x: number
      if ('touches' in e) {
        x = e.touches[0].pageX
      } else {
        x = e.pageX
      }

      let left = x - (container.getBoundingClientRect().left + window.scrollX)
      if (left < 0) {
        left = 0
      }
      let h: number
      if (left >= containerWidth) {
        // hue 360 and hue 0 is the same value, so it loses start/end with conversions
        h = 359.99
      } else {
        const percent = left / containerWidth
        h = percent * 360
      }

      if (hsl.h !== h) {
        const isPureBlack = hsl.l === 0 && hsl.s === 0
        const isPureWhite = hsl.l === 1 && hsl.s === 0

        if (isPureWhite) {
          // adjust pure white to bottom-right to allow for hue changes
          onChange({
            h,
            s: 0.01,
            l: 0.99,
            a: hsl.a,
            source: 'hsl',
          })
          return
        }

        if (isPureBlack) {
          // adjust pure white to top-right to allow for hue changes
          onChange({
            h,
            s: 0.01,
            l: 0.01,
            a: hsl.a,
            source: 'hsl',
          })
          return
        }

        onChange({
          h,
          s: hsl.s,
          l: hsl.l,
          a: hsl.a,
          source: 'hsl',
        })
      }
    },
    [onChange, hsl],
  )

  const handleHueChange = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      calculateHue(e)
    },
    [calculateHue],
  )

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true)
    handleHueChange(e)
  }

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging) {
        handleHueChange(e)
      }
    },
    [isDragging, handleHueChange],
  )

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true)
    handleHueChange(e)
  }

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault()
    if (isDragging) {
      handleHueChange(e)
    }
  }

  const clampLeft = useMemo(() => {
    const hue = Number(hsl.h)
    return `${(hue * 100) / 360}%`
  }, [hsl.h])

  return (
    <div
      className="relative h-4"
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseDown={handleMouseDown}
    >
      <div
        className="relative top-1 h-2 w-full cursor-pointer overflow-hidden rounded-full"
        ref={sliderRef}
        onMouseDown={handleMouseDown}
      >
        <div
          className="absolute h-2 w-full"
          style={{
            background:
              'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)',
          }}
        />
      </div>
      <div
        className="-translate-x-[50%] absolute top-0.5 cursor-grab rounded-full border-2 border-bgPrimaryBase bg-transparent p-1 shadow-md"
        style={{
          left: clampLeft,
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
      />
    </div>
  )
}
